import { THREE } from '@powerplay/common';
import { instancingFragmentShader, instancingVertexShader } from './shaders';
export class BatchingManager {
    tempMesh = new THREE.Mesh();
    layers = [[], [], [], [], []];
    objects;
    attributes = {
        positions: new Map(),
        orientations: new Map(),
        scales: new Map()
    };
    available = false;
    init(objects) {
        this.objects = objects;
    }
    batch(scene) {
        if (!this.available)
            return;
        let instances = 0;
        this.objects.forEach((value, key) => {
            instances = 0;
            value.data.forEach((valueE) => {
                if (!valueE.virtual)
                    instances += 1;
            });
            if (instances === 1) {
                const mesh = scene.getObjectByName(value.name);
                if (!mesh)
                    return;
                const object = this.objects.get(key);
                if (!object)
                    return;
                mesh.position.set(object.data[0].offset.x, object.data[0].offset.y, object.data[0].offset.z);
                mesh.rotation.set(object.data[0].rotation.x, object.data[0].rotation.y, object.data[0].rotation.z);
                mesh.scale.set(object.data[0].scale.x, object.data[0].scale.y, object.data[0].scale.z);
                mesh.updateMatrix();
                this.layers[value.layer].push(mesh);
            }
            else if (instances > 1 && this.available) {
                this.layers[value.layer].push(this.cloneObject(scene, key));
            }
            else {
                const mesh = scene.getObjectByName(value.name);
                mesh.visible = false;
            }
        });
    }
    cloneObject(scene, key) {
        const offsets = [];
        const orientations = [];
        const scales = [];
        const object = this.objects.get(key);
        const originalObject = scene.getObjectByName(key);
        if (!originalObject || !object)
            return new THREE.Mesh();
        object.data.forEach((value) => {
            if (!value.virtual) {
                offsets.push(value.offset.x, value.offset.y, value.offset.z);
                scales.push(value.scale.x, value.scale.y, value.scale.z);
                this.tempMesh.rotation.set(value.rotation.x, value.rotation.y, value.rotation.z);
                orientations.push(this.tempMesh.quaternion.x, this.tempMesh.quaternion.y, this.tempMesh.quaternion.z, this.tempMesh.quaternion.w);
            }
        });
        const bufferGeometry = originalObject.geometry;
        const geometry = new THREE.InstancedBufferGeometry();
        geometry.index = bufferGeometry.index;
        geometry.attributes.position = bufferGeometry.attributes.position;
        geometry.attributes.uv = bufferGeometry.attributes.uv;
        const positionAttr = new THREE.InstancedBufferAttribute(new Float32Array(offsets), 3);
        this.attributes.positions.set(key, positionAttr);
        const orientationAttr = new THREE.InstancedBufferAttribute(new Float32Array(orientations), 4);
        this.attributes.orientations.set(key, orientationAttr);
        const scaleAttr = new THREE.InstancedBufferAttribute(new Float32Array(scales), 3);
        this.attributes.scales.set(key, scaleAttr);
        geometry.setAttribute('offset', positionAttr);
        geometry.setAttribute('orientation', orientationAttr);
        geometry.setAttribute('scale', scaleAttr);
        const origMat = originalObject.material;
        const material = new THREE.RawShaderMaterial({
            uniforms: {
                map: {
                    value: origMat.map
                }
            },
            vertexShader: instancingVertexShader,
            fragmentShader: instancingFragmentShader,
            side: THREE.DoubleSide,
            transparent: origMat.transparent
        });
        const objectMesh = new THREE.Mesh(geometry, material);
        objectMesh.frustumCulled = false;
        objectMesh.matrixAutoUpdate = false;
        objectMesh.name = `BATCHED_${key}`;
        scene.add(objectMesh);
        originalObject.visible = false;
        return objectMesh;
    }
    setVisibilityForLayers(layer) {
        if (!this.available)
            return;
        this.layers.forEach((value, key) => {
            value.forEach((_, keyL) => {
                this.layers[key][keyL].visible = key <= layer;
            });
        });
    }
    change(scene, name, index, newIndex) {
        if (!this.available)
            return;
        const { positions, orientations, scales } = this.attributes;
        const iaP = positions.get(name);
        const iaR = orientations.get(name);
        const iaS = scales.get(name);
        const newObject = this.objects.get(name)?.data[newIndex];
        if (!newObject)
            return;
        if (!iaP || !iaR || !iaS) {
            const mesh = scene.getObjectByName(name);
            mesh.position.set(newObject.offset.x, newObject.offset.y, newObject.offset.z);
            mesh.rotation.set(newObject.rotation.x, newObject.rotation.y, newObject.rotation.z);
            mesh.scale.set(newObject.scale.x, newObject.scale.y, newObject.scale.z);
            mesh.updateMatrix();
            return;
        }
        this.tempMesh.rotation.set(newObject.rotation.x, newObject.rotation.y, newObject.rotation.z);
        const pos = positions.get(name);
        if (pos) {
            pos.setXYZ(index, newObject.offset.x, newObject.offset.y, newObject.offset.z);
            pos.needsUpdate = true;
        }
        const rot = orientations.get(name);
        if (rot) {
            rot.setXYZW(index, this.tempMesh.quaternion.x, this.tempMesh.quaternion.y, this.tempMesh.quaternion.z, this.tempMesh.quaternion.w);
            rot.needsUpdate = true;
        }
        const scale = scales.get(name);
        if (scale) {
            scale.setXYZ(index, newObject.scale.x, newObject.scale.y, newObject.scale.z);
            scale.needsUpdate = true;
        }
    }
    setNextVirtual(scene, name) {
        if (!this.available)
            return;
        const object = this.objects.get(name);
        if (!object)
            return;
        this.change(scene, name, object.lastVisibleObjectIndex, object.nextIndex);
        object.lastVisibleObjectIndex += 1;
        if (object.lastVisibleObjectIndex >= object.maxVisibleObjects) {
            object.lastVisibleObjectIndex = 0;
        }
        object.nextIndex += 1;
        if (object.nextIndex >= object.data.length)
            object.nextIndex = 0;
    }
    getObjectData(name, index) {
        const emptyObj = {
            offset: new THREE.Vector3(),
            rotation: new THREE.Vector3(),
            scale: new THREE.Vector3(),
            virtual: false
        };
        if (!this.available)
            return emptyObj;
        const object = this.objects.get(name);
        if (object !== undefined) {
            return object.data[index];
        }
        return emptyObj;
    }
    shiftYPosition(scene, gateIndex, name, posYoffset) {
        if (!this.available)
            return;
        const object = this.objects.get(name);
        if (!object)
            return;
        const dataIndex = gateIndex % object.maxVisibleObjects;
        const objectData = object.data[gateIndex];
        const { positions } = this.attributes;
        const iaP = positions.get(name);
        objectData.offset.y += posYoffset;
        if (!iaP) {
            const mesh = scene.getObjectByName(name);
            mesh.position.set(objectData.offset.x, objectData.offset.y, objectData.offset.z);
            mesh.updateMatrix();
            return;
        }
        const pos = positions.get(name);
        if (pos) {
            pos.setXYZ(dataIndex, objectData.offset.x, objectData.offset.y, objectData.offset.z);
            pos.needsUpdate = true;
        }
    }
    reset(scene) {
        this.objects.forEach(obj => {
            obj.lastVisibleObjectIndex = 0;
            obj.nextIndex = obj.maxVisibleObjects;
            for (let i = 0; i < obj.maxVisibleObjects; i++) {
                this.change(scene, obj.name, i, i);
            }
        });
    }
}
//# sourceMappingURL=BatchingManager.js.map